import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  MoreHorizontal,
  ChevronUp,
  Edit,
  Copy,
  Trash,
  Bed,
  ClockArrowDown,
  AlertCircle,
  GripVertical,
  BookmarkPlus
} from "lucide-react";
import { useState, useRef } from "react";
import { BaseActivity, ItineraryActivity } from "../models/activity.model";
import { getActivityTypeIcon } from "../models/activity-configs.model";
import { RichTextContent } from "@/components/ui/rich-text-content";
import { MediaCarousel } from "@/components/ui/MediaCarousel";
import { resolveWorldotaUrl } from "@/lib/builder/ratehawk/WorldotaImage";
import { useI18n } from "@/hooks/useI18n";

interface BaseActivityCardProps {
  activity: BaseActivity;
  onEdit: (activity: ItineraryActivity) => void;
  onDelete: (activity: ItineraryActivity) => void;
  onDuplicate?: (activity: ItineraryActivity) => void;
  onSaveToLibrary?: (activity: ItineraryActivity) => void;
  onExpand?: () => void;
  isReadOnly?: boolean;
  // Drag & drop handlers (optional)
  onDragStart?: (activity: ItineraryActivity) => void;
  onDropBefore?: (sourceActivityId: string, targetActivityId: string | undefined) => void;
  onDragEnd?: () => void;
  isTraveler?: boolean;
  isAdmin?: boolean;
  unsaved?: boolean;
  media?: string[];
  children: {
    alwaysVisible: React.ReactNode;
    collapsible: React.ReactNode;
  };
}

export const BaseActivityCard = ({
  activity,
  onEdit,
  onDelete,
  onDuplicate,
  onSaveToLibrary,
  onExpand,
  isReadOnly = false,
  onDragStart,
  onDropBefore,
  onDragEnd,
  children,
  isTraveler = false,
  isAdmin = false,
  unsaved = false,
  media,
}: BaseActivityCardProps) => {
  const { t } = useI18n();
  const [isExpanded, setIsExpanded] = useState(isTraveler || isReadOnly);
  const isOutActivity = activity.inOrOutActivity === 'out';
  const isInActivity = activity.inOrOutActivity === 'in';

  // ref used to store temporary drag image element so we can clean it up on drag end
  const dragImageRef = useRef<HTMLElement | null>(null);

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div
        data-activity-card={`activity-${activity.id}`}
        className="w-full mb-2 border flex items-center flex-row items-start border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm relative transition-colors duration-200 hover:border-gray-300"
      >

        {!(isTraveler || isReadOnly) && (
          <div className="pr-2 ml-1">
            {/* Drag handle - visible when parent provides drag handlers */}
            {onDragStart && onDropBefore && (
              <button
                aria-label="Drag to reorder"
                draggable={!isExpanded}
                onDragStart={(e) => {
                  if (isExpanded) return;
                  e.dataTransfer.setData('text/plain', activity.id);
                  e.dataTransfer.effectAllowed = 'move';

                  // Try to show the whole card as drag image. We clone the card element,
                  // position it offscreen, and pass it to setDragImage with proper offsets.
                  try {
                    const buttonEl = e.currentTarget as HTMLElement;
                    const cardEl = buttonEl.closest('[data-activity-card]') as HTMLElement | null;
                    if (cardEl && typeof e.dataTransfer.setDragImage === 'function') {
                      const rect = cardEl.getBoundingClientRect();
                      const clone = cardEl.cloneNode(true) as HTMLElement;
                      // style clone so it renders similarly and doesn't interfere with layout
                      clone.style.boxSizing = 'border-box';
                      clone.style.width = `${rect.width}px`;
                      clone.style.position = 'absolute';
                      clone.style.top = '-9999px';
                      clone.style.left = '-9999px';
                      clone.style.margin = '0';
                      clone.style.pointerEvents = 'none';
                      clone.style.opacity = '0.98';
                      // optional nice shadow so it appears lifted
                      clone.style.boxShadow = '0 10px 30px rgba(0,0,0,0.12)';

                      document.body.appendChild(clone);
                      dragImageRef.current = clone;

                      const offsetX = Math.max(0, (e.clientX - rect.left));
                      const offsetY = Math.max(0, (e.clientY - rect.top));

                      e.dataTransfer.setDragImage(clone, offsetX, offsetY);
                    }
                  } catch (err) {
                    // fallback: if setDragImage with element fails, silently ignore
                  }

                  if (onDragStart) onDragStart(activity as ItineraryActivity);
                }}
                onDragEnd={() => {
                  // clean up temporary drag image if exists
                  if (dragImageRef.current) {
                    try { document.body.removeChild(dragImageRef.current); } catch (e) { }
                    dragImageRef.current = null;
                  }

                  if (onDragEnd) onDragEnd();
                }}
                aria-disabled={isExpanded}
                className={`p-2 rounded w-8 h-8 flex items-center justify-center ${isExpanded ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-100 cursor-grab'}`}
              >
                <GripVertical className="h-4 w-4 text-gray-400" />
              </button>
            )}

          </div>
        )}
        <div className={`overflow-hidden flex-1 ${isTraveler ? '' : ''}`}>
          <div className={`${(isTraveler || isReadOnly) ? 'p-6 pb-0' : 'p-4 pl-2 pb-0'}`}>
          {/* Header with icon, title and menu */}
          <div className="flex items-center gap-3 flex-1">
            {/* Activity Type Icon */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-gray-200`}>
              {activity.type === 'accommodation' && isOutActivity
                ? <ClockArrowDown className="h-4 w-4" />
                : activity.type === 'accommodation' && isInActivity
                  ? <Bed className="h-4 w-4" />
                  : getActivityTypeIcon(activity.type)
              }
            </div>

            {/* Title */}
            <h4 className="text-gray-900 truncate flex-1">
              {activity.title}
              {activity.type === 'accommodation' && isInActivity && ' (Check-in)'}
              {activity.type === 'accommodation' && isOutActivity && ' (Check-out)'}
            </h4>

            {/* Unsaved indicator */}
            {unsaved && (
              <span className="flex items-center gap-1 shrink-0">
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 border border-red-300 text-red-700" title={t('common.notSaved')}>
                  <AlertCircle className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium hidden sm:inline">{t('common.notSaved')}</span>
                </span>
              </span>
            )}

            {/* Right side - Menu button - Only show if not read-only */}
            {!isReadOnly && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-12 h-12 rounded-full hover:bg-gray-100"
                  >
                    <MoreHorizontal className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem disabled={isOutActivity} onClick={() => onEdit(activity as ItineraryActivity)} className="flex items-center gap-2">
                    <Edit className="h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  {onDuplicate && (
                    <DropdownMenuItem disabled={isOutActivity} onClick={() => onDuplicate(activity as ItineraryActivity)} className="flex items-center gap-2">
                      <Copy className="h-4 w-4" />
                      Duplicate
                    </DropdownMenuItem>
                  )}
                  {onSaveToLibrary && (
                    <DropdownMenuItem disabled={isOutActivity} onClick={() => onSaveToLibrary(activity as ItineraryActivity)} className="flex items-center gap-2">
                      <BookmarkPlus className="h-4 w-4" />
                      {t('library.saveToLibraryAction')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => onDelete(activity as ItineraryActivity)}
                    disabled={isOutActivity}
                    className="flex items-center gap-2 text-red-600 focus:text-red-600"
                  >
                    <Trash className="h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          </div>

          {/* Activity Details - Always visible */}
          <div className={`flex flex-wrap md:flex-nowrap items-center gap-x-4 gap-y-1 xl:gap-x-8 mt-2 mb-2 text-gray-500 text-sm xl:text-base ${(isTraveler || isReadOnly) ? 'px-6' : 'px-4 pl-2'}`}>
            {children.alwaysVisible}
          </div>

          {/* Collapsible Content with animation */}
          <CollapsibleContent className="space-y-4 overflow-hidden transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
            <div className={`pt-2 ${(isTraveler || isReadOnly) ? 'px-6' : 'px-4 pl-2'}`}>
              {/* Media Carousel - show when expanded */}
              {media && media.length > 0 && (
                <div className="mb-4">
                  <MediaCarousel media={media.map(resolveWorldotaUrl)} imageSize="w-20 h-20 md:w-32 md:h-32" />
                </div>
              )}
              {activity.description && (
                <div className="mb-4">
                  <RichTextContent html={activity.description} />
                </div>
              )}
              {children.collapsible}
            </div>
          </CollapsibleContent>

          {/* Expand/Collapse Button */}
          <div className={`flex items-center justify-center sm:mt-2
             sm:mb-2 mt-4 mb-4 ${(isTraveler || isReadOnly) ? 'px-6' : 'px-4 pl-2'}`}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center gap-2 h-7 px-3 text-xs [&_svg]:size-3 xl:h-9 xl:px-4 xl:text-sm xl:[&_svg]:size-4"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    Less
                  </>
                ) : (
                  <>
                    <ChevronUp className="h-4 w-4 rotate-180" />
                    More
                  </>
                )}
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>
      </div>
    </Collapsible>
  );
};
